import { isEmpty } from 'lodash';
import React from 'react';
import { HtmlString } from '@adjust/dashboard-components';

import { Feature } from '@react/Pages/AccountSettingsPage/PricingPlanPage/features';
import { ChangeType } from '@redux/ducks/account/pricingPlan/interfaces';

import './ChangePricingPlanForm.scss';

export interface ChangePricingPlanFormProps {
  onCancel: () => void;
  onConfirm: () => void;
  attributions: string;
  attributionPrice: string;
  basePrice: string;
  features: Feature[];
  isUpgrade: boolean;
  changeType: ChangeType;
  isYearlyButNotCurrent: boolean;
  defaultContractLength: number;
  planName: string;
  nextBillingDate: string;
}

interface TranslationsProps {
  changeType: ChangeType;
  defaultContractLength: number;
  nextBillingDate: string;
}

const getTranslations = ({ changeType, defaultContractLength, nextBillingDate }: TranslationsProps) => {
  switch (changeType) {
    case ChangeType.Yearly: {
      return {
        featuresTitle: t('label.additional-features'),
        actionName: t('action.sign-n-months', {
          length: defaultContractLength,
        }),
        noticeMessage: t('pricing-plan.change-package.confirm.message'),
      };
    }
    case ChangeType.Choose: {
      return {
        featuresTitle: t('label.removed-features'),
        actionName: t('action.confirm-choice'),
        noticeMessage:
          t('pricing-plan.change-package.confirm.message') +
          '\n\n' +
          t('pricing-plan.change-package.additional-confirm.message', {
            date: nextBillingDate,
          }),
      };
    }
    case ChangeType.Upgrade: {
      return {
        featuresTitle: t('label.additional-features'),
        actionName: t('action.confirm-upgrade'),
        noticeMessage:
          t('pricing-plan.change-package.confirm.message') +
          '\n\n' +
          t('pricing-plan.change-package.additional-confirm.message', {
            date: nextBillingDate,
          }),
      };
    }
    case ChangeType.Downgrade: {
      return {
        featuresTitle: t('label.removed-features'),
        actionName: t('action.confirm-downgrade'),
        noticeMessage: t('pricing-plan.change-package.confirm-downgrade.message', { date: nextBillingDate }),
      };
    }
  }
};

const getHeadingTranslated = (isYearlyButNotCurrent: boolean, defaultContractLength: number, planName: string) =>
  isYearlyButNotCurrent
    ? t('pricing-plan.change-package.title', {
        length: defaultContractLength,
        packageName: planName,
      })
    : planName;

export const ChangePricingPlanForm = ({
  onCancel,
  onConfirm,
  features,
  isUpgrade,
  changeType,
  isYearlyButNotCurrent,
  defaultContractLength,
  planName,
  nextBillingDate,
  attributions,
  attributionPrice,
  basePrice,
}: ChangePricingPlanFormProps) => {
  const heading = getHeadingTranslated(isYearlyButNotCurrent, defaultContractLength, planName);

  const { featuresTitle, actionName, noticeMessage } = getTranslations({
    changeType,
    defaultContractLength,
    nextBillingDate,
  });

  const areFeaturesDefined = !isEmpty(features);

  return (
    <div className="change-pricing-plan-form">
      <h1 className="change-pricing-plan-form__header">{heading}</h1>

      <div className="change-pricing-plan-form__package">
        <h4 className="change-pricing-plan-form__title">{t('label.attribution-and-pricing')}</h4>

        <div className="change-pricing-plan-form__details">
          <div className="flex-one change-pricing-plan-form__detail-box">
            <div className="change-pricing-plan-form__detail-content">
              <div className="change-pricing-plan-form__detail-title">{t('pricing-plan.included-attributions')}</div>
              <div className="change-pricing-plan-form__detail-value">{attributions}</div>
            </div>
          </div>
          <div className="flex-one change-pricing-plan-form__detail-box">
            <div className="change-pricing-plan-form__detail-content">
              <div className="change-pricing-plan-form__detail-title">{t('pricing-plan.additional-attributions')}</div>
              <div className="change-pricing-plan-form__detail-value">{attributionPrice}</div>
            </div>
          </div>
          <div className="flex-one change-pricing-plan-form__detail-box">
            <div className="change-pricing-plan-form__detail-content">
              <div className="change-pricing-plan-form__detail-title">{t('pricing-plan.monthly-fee')}</div>
              <div className="change-pricing-plan-form__detail-value">{basePrice}</div>
            </div>
          </div>
        </div>

        {areFeaturesDefined && (
          <div className="change-pricing-plan-form__features">
            <h4 className="change-pricing-plan-form__title">{featuresTitle}</h4>

            <div
              className={`flex-box-row-wrap change-pricing-plan-form__details ${
                isUpgrade
                  ? 'change-pricing-plan-form__details--upgrade'
                  : 'change-pricing-plan-form__details--downgrade'
              }`}
            >
              {features.map(feature => (
                <div className="flex-one change-pricing-plan-form__detail-box" key={feature.key}>
                  <div className="change-pricing-plan-form__detail-content">
                    <div className="flex-box-row flex-align-center change-pricing-plan-form__detail-wrap">
                      <i
                        className={`change-pricing-plan-form__icon ${
                          isUpgrade ? 'icon-plus-round' : 'icon-minus-round'
                        }`}
                      />
                      <span className="change-pricing-plan-form__feature-label">{feature.label}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="change-pricing-plan-form__notice-message">
          <HtmlString>{noticeMessage}</HtmlString>
        </div>

        <div
          className={`flex-box-row change-pricing-plan-form__action change-pricing-plan-form__action--${changeType}`}
        >
          <button className="btn flex-one" onClick={onCancel}>
            {t('action.cancel')}
          </button>
          <button className="btn btn-main flex-one" onClick={onConfirm}>
            {actionName}
          </button>
        </div>
      </div>
    </div>
  );
};
